class OrderService {
    axiosInstance;
    constructor(axiosInstance) {
        this.axiosInstance = axiosInstance;
    }
    adminPortal = {
        fetch: async (orderID) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/admin-portal/${orderID}`);
            return data;
        },
        query: async (query) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/admin-portal`, {
                params: query,
            });
            return data;
        },
        create: async (input) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/order/admin-portal`, input);
            return data;
        },
        update: async (orderID, input) => {
            const { data } = await this.axiosInstance.mainInstance.put(`/order/admin-portal/${orderID}`, input);
            return data;
        },
        subscription: {
            query: (query) => {
                return this.axiosInstance.socket.connect('/order/admin-portal', {
                    params: query,
                });
            },
            fetch: (orderID) => {
                return this.axiosInstance.socket.connect(`/order/admin-portal/${orderID}`);
            },
        },
        image: {
            create: async (orderID, file) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/admin-portal/${orderID}/image`, file);
                return data;
            },
            delete: async (orderID, file) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/order/admin-portal/${orderID}/image`, {
                    data: file,
                });
                return data;
            },
            update: async (orderID, file) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/order/admin-portal/${orderID}/image`, file);
                return data;
            },
        },
        /**
         * Delete a file from S3 via API
         * @param file - File object to be uploaded
         * @param orderID - Order ID
         * @deprecated Use `image.delete` instead
         */
        deleteOrderImage: async (orderID, file) => {
            const response = await this.axiosInstance.mainInstance({
                method: 'delete',
                url: `/order/admin-portal/${orderID}/image`,
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    ...file,
                },
            });
            return response;
        },
        /**
         * Add an image to an order via API
         * @deprecated Use `image.create` instead
         */
        addImageToOrder: async (orderID, file) => {
            const response = await this.axiosInstance.mainInstance({
                method: 'post',
                url: `/order/admin-portal/${orderID}/image`,
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    ...file,
                },
            });
            return response;
        },
        addSwitch: async (orderSwitch) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/order/admin-portal/switch`, orderSwitch);
            return data;
        },
        deleteSwitch: async (id) => {
            const { data } = await this.axiosInstance.mainInstance.delete(`/order/admin-portal/${id}/switch`, {});
            return data;
        },
        notes: {
            create: async (id, input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/admin-portal/${id}/internal-note`, input);
                return data;
            },
        },
        changeRequest: {
            /**
             * Creates a new change request for an order.
             * @param request - The request to create.
             * @returns A promise that resolves to the created change request.
             */
            create: async (request) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/admin-portal/change-request`, request);
                return data;
            },
            /**
             * Updates an existing change request for an order.
             * @param request - The updated details of the change request, including its ID.
             * @returns A promise that resolves to the updated change request.
             */
            update: async (request) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/order/admin-portal/change-request/${request.id}`, request);
                return data;
            },
            query: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.get('/order/admin-portal/change-request', {
                    params: input,
                });
                return data;
            },
            subscription: {
                query: (query) => {
                    let params = undefined;
                    if (query && query.status && query.status.length > 0) {
                        params = {
                            ...query,
                            status: query.status.join(','),
                        };
                    }
                    else if (query) {
                        params = {
                            ...query,
                            status: '',
                        };
                    }
                    else {
                        params = query;
                    }
                    return this.axiosInstance.socket.connect('/order/admin-portal/change-request', {
                        params: params,
                    });
                },
            },
        },
        ocr: {
            /**
             * Upload a new image and process it with OCR
             */
            upload: async (orderID, file) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/${orderID}/ocr/upload`, file);
                return data;
            },
            /**
             * Trigger or re-run OCR on an already uploaded image
             */
            trigger: async (orderID, input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/${orderID}/ocr/trigger`, input);
                return data;
            },
            /**
             * Retrieve the OCR result for an image
             */
            getResult: async (orderID, imageId) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/order/${orderID}/ocr/result`, {
                    params: { imageId },
                });
                return data;
            },
        },
    };
    /**
     * Represents the customer portal functionality of the Order service.
     */
    customerPortal = {
        /**
         * Retrieves a customer's order by its ID.
         * @param orderID - The ID of the order to retrieve.
         * @returns A promise that resolves to the retrieved order.
         */
        fetch: async (orderID) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/customer-portal/${orderID}`);
            return data;
        },
        /**
         * Retrieves a list of orders that match the given query.
         * @param query - The query to use to filter the orders.
         * @returns A promise that resolves to a paginated result of orders matching the query.
         */
        query: async (query) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/customer-portal`, {
                params: query,
            });
            return data;
        },
        /**
         * Subroutes for change requests
         */
        changeRequest: {
            /**
             * Creates a new change request for an order.
             * @param request - The request to create.
             * @returns A promise that resolves to the created change request.
             */
            create: async (request) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/customer-portal/change-request`, request);
                return data;
            },
            /**
             * Updates an existing change request for an order.
             * @param request - The updated details of the change request, including its ID.
             * @returns A promise that resolves to the updated change request.
             */
            update: async (request) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/order/customer-portal/change-request/${request.id}`, request);
                return data;
            },
        },
        summary: async () => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/customer-portal/summary`);
            return data;
        },
    };
    wayste = {
        fetch: async (orderID) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/${orderID}`);
            return data;
        },
        query: async (query) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order`, {
                params: query,
            });
            return data;
        },
        create: async (input) => {
            const { data } = await this.axiosInstance.mainInstance.post('/order', input);
            return data;
        },
        update: async (orderID, input) => {
            const { data } = await this.axiosInstance.mainInstance.put(`/order/${orderID}`, input);
            return data;
        },
        addInternalNote: async (orderID, note) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/order/${orderID}/add-internal-note`, {
                note: note,
            });
            return data;
        },
        addImage: async (orderID, file) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/order/${orderID}/add-image`, file);
            return data;
        },
        fetchSchedules: async (orderID) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/order/${orderID}/schedule`);
            return data;
        },
        assignContainer: async (orderID, params) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/order/${orderID}/assign-container`, params);
            return data;
        },
        switch: {
            create: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post('/order/switch', input);
                return data;
            },
            update: async (switchId, input) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/order/switch/${switchId}`, input);
                return data;
            },
            delete: async (switchId) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/order/switch/${switchId}`);
                return data;
            },
        },
        subscription: {
            query: (query) => {
                return this.axiosInstance.socket.connect('/order', {
                    params: query,
                });
            },
            fetch: (orderID) => {
                return this.axiosInstance.socket.connect(`/order/${orderID}`);
            },
        },
        image: {
            create: async (orderID, file) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/${orderID}/add-image`, file);
                return data;
            },
            delete: async (orderID, file) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/order/${orderID}/remove-image`, {
                    data: file,
                });
                return data;
            },
        },
        orderRequest: {
            update: async (orderRequestID, input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/request/${orderRequestID}`, input);
                return data;
            },
            subscription: {
                query: () => {
                    return this.axiosInstance.socket.connect('/order/request');
                },
            },
        },
        workRequest: {
            update: async (workRequestID, input) => {
                const { data } = await this.axiosInstance.mainInstance.patch(`/order/work-request/${workRequestID}`, input);
                return data;
            },
            subscription: {
                query: () => {
                    return this.axiosInstance.socket.connect('/order/work-request');
                },
            },
        },
        schedule: {
            subscription: {
                scheduler: (date) => {
                    if (date.length !== 10) {
                        throw new Error('Invalid date');
                    }
                    return this.axiosInstance.socket.connect(`/order/ro-scheduler/${date}`);
                },
                fetch: (date) => {
                    if (date.length !== 10) {
                        throw new Error('Invalid date');
                    }
                    return this.axiosInstance.socket.connect(`/order/schedule/${date}`);
                },
            },
            query: async (query) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/order/schedule`, {
                    params: query,
                });
                return data;
            },
            /**
             * It takes a `ReorderScheduleInput` object as an argument, and returns a `DailySchedule` object
             * @param {ReorderScheduleInput} input - ReorderScheduleInput
             * @returns DailySchedule
             */
            update: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/schedule/reorder`, input);
                return data;
            },
        },
        driverTask: {
            subscription: {
                query: (query) => {
                    return this.axiosInstance.socket.connect('/order/schedule-entry', {
                        params: query,
                    });
                },
            },
            query: async (query) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/order/schedule-entry`, {
                    params: query,
                });
                return data;
            },
            create: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/order/schedule-entry`, input);
                return data;
            },
            update: async (driverTaskID, input) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/order/schedule-entry/${driverTaskID}`, input);
                return data;
            },
            delete: async (driverTaskID) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/order/schedule-entry/${driverTaskID}`);
                return data;
            },
        },
    };
}
export default OrderService;
//# sourceMappingURL=Order.service.js.map